import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Code2,
  Globe2,
  Building2,
  Workflow,
  Sparkles,
  ShieldCheck,
  Layers,
  GitBranch,
  Gauge,
  Telescope,
  Compass,
  Hammer,
  Repeat,
  Rocket,
  Mail,
  MapPin,
  Check,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LogoMark, LogoLockup } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Approach", href: "#approach" },
  { label: "Process", href: "#process" },
  { label: "Clients", href: "#clients" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  {
    icon: Code2,
    title: "Custom Software Development",
    description:
      "Production-grade applications built around your data, your rules, and your edge cases — not a template that almost fits.",
    bullets: ["Internal tools & dashboards", "APIs & data pipelines", "SaaS platforms"],
  },
  {
    icon: Globe2,
    title: "Website & Web App Development",
    description:
      "Fast, accessible, and fully responsive sites that load quickly, rank well, and convert — from marketing pages to full product apps.",
    bullets: ["Marketing & brand sites", "Customer portals", "Headless commerce"],
  },
  {
    icon: Building2,
    title: "Enterprise Solutions",
    description:
      "Secure, auditable systems engineered for teams, compliance, and scale — integrated with the tools you already run on.",
    bullets: ["SSO, RBAC, audit logs", "Legacy modernization", "High-availability deployments"],
  },
  {
    icon: Workflow,
    title: "Workflow Automation & Integration",
    description:
      "Connect the gaps between CRMs, ERPs, spreadsheets, and vendors. Replace copy-paste work with systems that run on their own.",
    bullets: ["API & webhook integration", "ETL + reporting", "AI-assisted automations"],
  },
];

const DIFFERENTIATORS = [
  {
    icon: Layers,
    title: "Built to your workflow — not the other way around",
    body: "We map your actual process before writing code. The software adapts to how your team already works, then removes the friction.",
  },
  {
    icon: Gauge,
    title: "Scales with your business",
    body: "Solo founder today, 200 employees in three years? Architectures we ship hold up under real growth — without a rewrite.",
  },
  {
    icon: GitBranch,
    title: "Integrations that actually work",
    body: "QuickBooks, HubSpot, Stripe, Salesforce, custom APIs — we stitch your stack together so data flows where it needs to.",
  },
  {
    icon: ShieldCheck,
    title: "Secure by default",
    body: "Authentication, role-based access, audit trails, and backups are part of v1 — not a patch added later when something breaks.",
  },
  {
    icon: Sparkles,
    title: "A partner, not a contractor",
    body: "We think in quarters, not tickets. Expect a team that challenges assumptions, explains trade-offs, and stays engaged past launch.",
  },
];

const PROCESS = [
  {
    step: "01",
    icon: Telescope,
    title: "Discover",
    body: "We sit down with your team, map current workflows, and identify where software can create measurable leverage.",
  },
  {
    step: "02",
    icon: Compass,
    title: "Design",
    body: "Clickable prototypes and system architecture — aligned on scope, cost, and outcomes before a single line of production code.",
  },
  {
    step: "03",
    icon: Hammer,
    title: "Build",
    body: "Weekly demos, production-ready increments, and transparent engineering. You always know what's shipping next.",
  },
  {
    step: "04",
    icon: Repeat,
    title: "Iterate",
    body: "Real users, real feedback. We refine based on what the data says, not what a spec doc assumed six months ago.",
  },
  {
    step: "05",
    icon: Rocket,
    title: "Launch & Support",
    body: "We handle deployment, monitoring, and handoff — and stay on-call as your product evolves.",
  },
];

const CLIENTS = [
  {
    label: "Small Businesses",
    description:
      "A single tool that replaces three spreadsheets and two browser tabs — sized for teams of 2 to 20.",
    metric: "Launch in 6–10 weeks",
  },
  {
    label: "Growing Companies",
    description:
      "Systems that remove bottlenecks as you scale past 50 employees and your first ops hires.",
    metric: "Ships to production every week",
  },
  {
    label: "Enterprise Organizations",
    description:
      "Mission-critical platforms with SSO, compliance, and integrations to existing ERP / CRM stacks.",
    metric: "SOC 2–ready architectures",
  },
];

/* -------- Subtle reveal helper -------- */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        transitionDelay: `${delay}ms`,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        opacity: visible ? 1 : 0,
        transition:
          "opacity 900ms cubic-bezier(0.16,1,0.3,1), transform 900ms cubic-bezier(0.16,1,0.3,1)",
      }}
      className={className}
    >
      {children}
    </div>
  );
};

/* -------- Header -------- */
const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/60"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <LogoMark size={36} />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-[15px] font-semibold tracking-tight text-slate-100">
              Cleveland Creative
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400/80 mt-1">
              Group
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm text-slate-400 hover:text-cyan-300 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="#contact"
            data-testid="header-cta"
            className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-cyan-400 text-slate-950 font-semibold text-sm hover:bg-cyan-300 transition-all shadow-[0_0_0_1px_rgba(0,229,255,0.4),0_8px_30px_-8px_rgba(0,229,255,0.6)]"
          >
            Start a Project
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 rounded-md text-slate-200 hover:bg-slate-900"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-800/60 bg-slate-950/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-slate-300 py-2 text-sm"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 h-11 rounded-full bg-cyan-400 text-slate-950 font-semibold text-sm"
            >
              Start a Project <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* -------- Hero -------- */
const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative overflow-hidden"
  >
    {/* Background layers */}
    <div className="absolute inset-0 bg-grid mask-fade-b pointer-events-none" />
    <div className="absolute inset-0 bg-radial-fade pointer-events-none" />
    <div className="absolute -top-32 -left-40 h-[520px] w-[520px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
    <div className="absolute -top-20 right-0 h-[420px] w-[420px] rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-28 md:pt-28 md:pb-40">
      <Reveal>
        <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full border border-slate-800 bg-slate-900/70 backdrop-blur text-xs text-slate-300">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse-ring" />
          <span className="font-mono tracking-wider uppercase">
            Cleveland, OH · Est. software studio
          </span>
        </div>
      </Reveal>

      <Reveal delay={80}>
        <h1
          data-testid="hero-headline"
          className="font-display mt-8 text-5xl md:text-7xl lg:text-[88px] leading-[0.95] font-black tracking-tight text-balance max-w-5xl"
        >
          Custom software,{" "}
          <span className="text-gradient-cyan">engineered to fit</span>{" "}
          the way your business actually works.
        </h1>
      </Reveal>

      <Reveal delay={180}>
        <p
          data-testid="hero-subheadline"
          className="mt-7 max-w-2xl text-lg md:text-xl text-slate-400 leading-relaxed"
        >
          We design and build web applications, internal tools, and enterprise
          systems for teams that have outgrown off-the-shelf — from small
          businesses to enterprise organizations.
        </p>
      </Reveal>

      <Reveal delay={260}>
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <a
            href="#contact"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-2 h-12 px-7 rounded-full bg-cyan-400 text-slate-950 font-bold text-sm tracking-wide transition-all duration-300 hover:bg-cyan-300 shadow-[0_0_0_1px_rgba(0,229,255,0.4),0_20px_50px_-10px_rgba(0,229,255,0.55)] hover:-translate-y-0.5"
          >
            Start a Project
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href="#services"
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center gap-2 h-12 px-7 rounded-full border border-slate-700 text-slate-100 font-semibold text-sm hover:border-cyan-400/60 hover:text-cyan-300 transition-all duration-300"
          >
            Explore services
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </Reveal>

      {/* Stat / proof strip */}
      <Reveal delay={380}>
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-slate-800 bg-slate-800/40 max-w-4xl">
          {[
            { k: "12+ yrs", v: "Building production software" },
            { k: "40+", v: "Applications shipped" },
            { k: "SMB → Enterprise", v: "Scaled across team sizes" },
            { k: "<6 wk", v: "Typical first release" },
          ].map((s) => (
            <div
              key={s.k}
              className="bg-slate-950 p-5 md:p-6 flex flex-col gap-1"
            >
              <div className="font-display font-bold text-xl md:text-2xl text-slate-50">
                {s.k}
              </div>
              <div className="text-xs md:text-sm text-slate-400">{s.v}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>

    {/* Bottom glow line */}
    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
  </section>
);

/* -------- Services -------- */
const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="relative py-24 md:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <Reveal>
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
              01 · Services
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight max-w-2xl text-balance">
              Four practices, one engineering team.
            </h2>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <p className="text-slate-400 max-w-md text-base md:text-lg">
            Every project is scoped to the problem at hand. We don't force your
            business into a product template.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.title} delay={i * 90}>
              <article
                data-testid={`service-card-${i}`}
                className="group relative h-full rounded-2xl border border-slate-800 bg-slate-900/60 p-8 md:p-10 overflow-hidden transition-all duration-500 hover:border-cyan-400/40 hover:-translate-y-1 hover:shadow-cyan-glow"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_var(--x,20%)_0%,rgba(0,229,255,0.12),transparent_50%)]" />
                <div className="relative flex items-start justify-between gap-6 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-slate-950 border border-slate-800 grid place-items-center text-cyan-300 group-hover:border-cyan-400/50 transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-slate-600 group-hover:text-cyan-400 transition-colors">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="relative font-display text-xl md:text-2xl font-semibold text-slate-50 mb-3 tracking-tight">
                  {s.title}
                </h3>
                <p className="relative text-slate-400 leading-relaxed mb-6">
                  {s.description}
                </p>
                <ul className="relative flex flex-wrap gap-2">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-xs font-medium px-3 py-1.5 rounded-full bg-slate-950 border border-slate-800 text-slate-300"
                    >
                      {b}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* -------- Value Prop -------- */
const ValueProp = () => (
  <section
    id="approach"
    data-testid="value-prop-section"
    className="relative py-24 md:py-32 border-y border-slate-900"
  >
    <div className="absolute inset-0 bg-grid-fine opacity-40 pointer-events-none" />
    <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[380px] w-[680px] bg-cyan-500/[0.07] blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="sticky top-28">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
                02 · Approach
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight text-balance mb-6">
                Software that bends to your workflow.
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Most off-the-shelf tools force your business to change. We do
                the opposite — study how your team already works, then build
                around it.
              </p>
              <a
                href="#process"
                className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-semibold text-sm group"
              >
                See how we work
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 space-y-4">
          {DIFFERENTIATORS.map((d, i) => {
            const Icon = d.icon;
            return (
              <Reveal key={d.title} delay={i * 80}>
                <div
                  data-testid={`diff-${i}`}
                  className="group relative flex gap-5 rounded-2xl border border-slate-800 bg-slate-900/50 p-6 md:p-7 hover:border-cyan-400/30 hover:bg-slate-900 transition-all duration-300"
                >
                  <div className="flex-shrink-0 h-11 w-11 rounded-lg bg-slate-950 border border-slate-800 grid place-items-center text-cyan-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg md:text-xl font-semibold text-slate-50 mb-1.5">
                      {d.title}
                    </h3>
                    <p className="text-slate-400 text-[15px] leading-relaxed">
                      {d.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

/* -------- Process -------- */
const Process = () => (
  <section
    id="process"
    data-testid="process-section"
    className="relative py-24 md:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <div className="max-w-2xl mb-16">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
            03 · Process
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight text-balance mb-5">
            From first call to production — predictable at every step.
          </h2>
          <p className="text-slate-400 text-lg">
            Five checkpoints. Weekly demos. No six-month surprises.
          </p>
        </div>
      </Reveal>

      <div className="relative">
        {/* Vertical timeline spine */}
        <div className="absolute left-[22px] md:left-1/2 md:-translate-x-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-cyan-500/40 to-transparent pointer-events-none hidden md:block" />
        <div className="absolute left-[22px] top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-cyan-500/40 to-transparent pointer-events-none md:hidden" />

        <ol className="space-y-6 md:space-y-10">
          {PROCESS.map((p, i) => {
            const Icon = p.icon;
            const rightSide = i % 2 === 1;
            return (
              <Reveal key={p.step} delay={i * 80}>
                <li
                  data-testid={`process-step-${i}`}
                  className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center`}
                >
                  {/* Node */}
                  <span className="absolute left-[15px] md:left-1/2 md:-translate-x-1/2 top-6 h-4 w-4 rounded-full bg-cyan-400 ring-4 ring-cyan-400/20" />

                  <div
                    className={`pl-14 md:pl-0 ${
                      rightSide ? "md:order-2 md:pl-12" : "md:pr-12 md:text-right"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-3 mb-3 ${
                        rightSide ? "" : "md:justify-end"
                      }`}
                    >
                      <span className="font-mono text-xs text-cyan-400">
                        STEP {p.step}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-50 tracking-tight mb-3">
                      {p.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed max-w-md md:inline-block">
                      {p.body}
                    </p>
                  </div>

                  <div
                    className={`hidden md:block ${
                      rightSide ? "md:order-1 md:pr-12 md:text-right" : "md:pl-12"
                    }`}
                  >
                    <div
                      className={`inline-flex items-center gap-3 px-5 py-3 rounded-xl border border-slate-800 bg-slate-900/70 ${
                        rightSide ? "" : ""
                      }`}
                    >
                      <span className="h-10 w-10 rounded-lg bg-slate-950 border border-slate-800 grid place-items-center text-cyan-300">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="font-mono text-xs uppercase tracking-widest text-slate-400">
                        {p.title}
                      </span>
                    </div>
                  </div>
                </li>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </div>
  </section>
);

/* -------- Target Clients -------- */
const Clients = () => (
  <section
    id="clients"
    data-testid="clients-section"
    className="relative py-24 md:py-32 bg-slate-950"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <div className="max-w-3xl mb-14">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
            04 · Who we build for
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight text-balance">
            Sized for your stage — not locked to it.
          </h2>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {CLIENTS.map((c, i) => (
          <Reveal key={c.label} delay={i * 100}>
            <div
              data-testid={`client-${i}`}
              className="group relative h-full rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/80 to-slate-950 p-8 overflow-hidden transition-all duration-300 hover:border-cyan-400/30"
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="font-mono text-xs text-slate-500 mb-6">
                0{i + 1}
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-50 tracking-tight mb-4">
                {c.label}
              </h3>
              <p className="text-slate-400 leading-relaxed mb-8">
                {c.description}
              </p>
              <div className="pt-6 border-t border-slate-800 flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-cyan-400" />
                <span className="text-slate-300 font-medium">{c.metric}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* -------- Contact -------- */
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    budget: "",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.details) {
      toast.error("Please fill in name, email, and project details.");
      return;
    }
    if (form.details.length < 10) {
      toast.error("Please share a bit more about the project (10+ chars).");
      return;
    }
    try {
      setSubmitting(true);
      const payload = { ...form };
      if (!payload.company) delete payload.company;
      if (!payload.project_type) delete payload.project_type;
      if (!payload.budget) delete payload.budget;

      await axios.post(`${API}/contact`, payload);
      toast.success("Thanks — we'll be in touch within one business day.");
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        company: "",
        project_type: "",
        budget: "",
        details: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email jamursch@gmail.com.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid mask-fade-b opacity-60 pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
                05 · Start a project
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-balance mb-6">
                Tell us what you're{" "}
                <span className="text-gradient-cyan">trying to build.</span>
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-10">
                One short form. A real engineer reads every message — we'll
                follow up within one business day with next steps or honest
                feedback on fit.
              </p>
            </Reveal>

            <Reveal delay={120}>
              <div className="space-y-4">
                <div className="p-5 rounded-xl border border-slate-800 bg-slate-900/60">
                  <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mb-2">
                    Response time
                  </div>
                  <div className="text-slate-100 font-medium leading-relaxed">
                    A real engineer replies within one business day — usually
                    the same afternoon.
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={80}>
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="relative rounded-2xl border border-slate-800 bg-slate-900/70 backdrop-blur p-6 md:p-10 shadow-cyan-glow"
              >
                {submitted && (
                  <div className="mb-6 flex items-start gap-3 rounded-lg border border-cyan-400/30 bg-cyan-500/5 p-4 text-sm text-cyan-100">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-cyan-300" />
                    <div>
                      <div className="font-semibold">Message received.</div>
                      <div className="text-cyan-200/80">
                        We'll reply within one business day.
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label
                      htmlFor="name"
                      className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block"
                    >
                      Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="input-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jordan Mitchell"
                      className="bg-slate-950 border-slate-800 h-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="email"
                      className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block"
                    >
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="input-email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="jordan@company.com"
                      className="bg-slate-950 border-slate-800 h-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="company"
                      className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block"
                    >
                      Company
                    </Label>
                    <Input
                      id="company"
                      data-testid="input-company"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Acme Industries"
                      className="bg-slate-950 border-slate-800 h-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block">
                      Project type
                    </Label>
                    <Select
                      value={form.project_type}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, project_type: v }))
                      }
                    >
                      <SelectTrigger
                        data-testid="input-project-type"
                        className="bg-slate-950 border-slate-800 h-11 text-slate-100 focus:ring-cyan-400 focus:ring-offset-0"
                      >
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                        <SelectItem value="custom-software">Custom Software</SelectItem>
                        <SelectItem value="website-webapp">Website / Web App</SelectItem>
                        <SelectItem value="enterprise">Enterprise Solution</SelectItem>
                        <SelectItem value="automation">Workflow Automation</SelectItem>
                        <SelectItem value="other">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block">
                      Budget range
                    </Label>
                    <Select
                      value={form.budget}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, budget: v }))
                      }
                    >
                      <SelectTrigger
                        data-testid="input-budget"
                        className="bg-slate-950 border-slate-800 h-11 text-slate-100 focus:ring-cyan-400 focus:ring-offset-0"
                      >
                        <SelectValue placeholder="Optional — helps us scope" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                        <SelectItem value="under-15k">Under $15k</SelectItem>
                        <SelectItem value="15-50k">$15k – $50k</SelectItem>
                        <SelectItem value="50-150k">$50k – $150k</SelectItem>
                        <SelectItem value="150k+">$150k+</SelectItem>
                        <SelectItem value="tbd">Still figuring that out</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label
                      htmlFor="details"
                      className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block"
                    >
                      Project details *
                    </Label>
                    <Textarea
                      id="details"
                      data-testid="input-details"
                      value={form.details}
                      onChange={update("details")}
                      placeholder="What are you trying to build or fix? What's the current workflow look like?"
                      rows={5}
                      className="bg-slate-950 border-slate-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400 resize-none"
                    />
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-xs text-slate-500 max-w-sm">
                    By submitting, you agree we can reach out via email. We
                    don't share your info — ever.
                  </p>
                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="h-12 px-8 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-sm disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_0_0_1px_rgba(0,229,255,0.35),0_14px_40px_-12px_rgba(0,229,255,0.55)]"
                  >
                    {submitting ? "Sending…" : "Send project brief"}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------- Footer -------- */
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative border-t border-slate-900 bg-slate-950"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-16">
        <div className="md:col-span-6">
          <div className="mb-8">
            <LogoLockup size={64} />
          </div>
          <div className="font-display text-4xl md:text-6xl font-black tracking-tight leading-[0.95] text-slate-50 text-balance">
            Cleveland Creative{" "}
            <span className="text-gradient-cyan">Group.</span>
          </div>
          <p className="mt-6 max-w-md text-slate-400">
            Custom software & web development for businesses that have outgrown
            off-the-shelf.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">
            Navigate
          </div>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  className="text-slate-300 hover:text-cyan-300 transition-colors text-sm"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">
            Contact
          </div>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                href="mailto:jamursch@gmail.com"
                className="text-slate-300 hover:text-cyan-300 transition-colors"
                data-testid="footer-email"
              >
                jamursch@gmail.com
              </a>
            </li>
            <li className="text-slate-400">Cleveland, Ohio</li>
            <li>
              <a
                href="#contact"
                className="inline-flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200"
              >
                Start a project <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="pt-8 border-t border-slate-900 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="text-xs text-slate-500 font-mono">
          © {new Date().getFullYear()} Cleveland Creative Group · All rights
          reserved.
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
          <a
            href="/privacy"
            data-testid="footer-privacy"
            className="text-slate-400 hover:text-cyan-300 transition-colors"
          >
            Privacy Policy
          </a>
          <a
            href="/terms"
            data-testid="footer-terms"
            className="text-slate-400 hover:text-cyan-300 transition-colors"
          >
            Terms of Service
          </a>
          <a
            href="/sms-terms"
            data-testid="footer-sms-terms"
            className="text-slate-400 hover:text-cyan-300 transition-colors"
          >
            SMS Terms &amp; Consent
          </a>
          <span className="text-slate-600 font-mono">
            Engineered in Cleveland, OH
          </span>
        </div>
      </div>
    </div>
  </footer>
);

/* -------- Page -------- */
const Landing = () => {
  return (
    <main className="relative bg-slate-950 text-slate-50 min-h-screen">
      <Header />
      <Hero />
      <Services />
      <ValueProp />
      <Process />
      <Clients />
      <Contact />
      <Footer />
    </main>
  );
};

export default Landing;
